import React, { useEffect, useState } from 'react'
import { Button } from 'semantic-ui-react'
import styled from 'styled-components'
import { completeTask, reopenTask } from 'utility/services/crm'
import { formatMoney, formatShortDate, relativeDays } from 'utility/crm/dates'
import { safeLink } from 'utility/crm/health'
import {
    ALL_CRM_SCOPES,
    invalidateCrm,
    useAccountDetail,
    useContact,
    useContactActivities,
    useContactDeals,
    useContactTasks,
    useCrmToast,
} from 'hooks/crm'
import { CrmDrawer } from './CrmOverlay'
import { ContactForm } from './ContactForm'
import { QuickLog } from './QuickLog'
import { EmailDialog } from './EmailDialog'
import { MeetingDialog } from './MeetingDialog'
import { TaskDialog } from './TaskDialog'
import { AsyncState, Avatar, Card, ChipButton, Muted, Pill, Row, StagePill, TabBar, TabButton } from './ui'

const TABS = [
    ['activity', 'Activity'],
    ['details', 'Details'],
    ['company', 'Company'],
    ['deals', 'Deals'],
]

const TYPE_LABEL = { note: 'Note', call: 'Call', email: 'Email', meeting: 'Meeting', stage: 'Stage change', task: 'Task', system: 'System' }
const TIMELINE_FILTERS = [
    ['all', 'All'],
    ['note', 'Note'],
    ['call', 'Call'],
    ['email', 'Email'],
    ['meeting', 'Meeting'],
    ['stage', 'Stage change'],
]

const Entry = styled.div`
    padding: 10px 0;
    border-bottom: 1px solid rgba(128, 128, 128, 0.25);
    .head {
        display: flex;
        justify-content: space-between;
        gap: 8px;
        font-weight: 600;
    }
    .body {
        white-space: pre-wrap;
        overflow-wrap: anywhere;
        margin: 4px 0;
    }
`

const HEALTH_TONE = { green: 'green', amber: 'amber', red: 'red' }

const FollowUps = ({ contactId }) => {
    const tasks = useContactTasks(contactId)
    const { successToast, showError } = useCrmToast()
    const [busy, setBusy] = useState(null)
    const list = tasks.data || []
    const sorted = [...list].sort((a, b) => Number(a.IsDone) - Number(b.IsDone) || String(a.DueDate).localeCompare(String(b.DueDate)))

    const toggle = async task => {
        setBusy(task.TaskId)
        try {
            if (task.IsDone) await reopenTask(task.TaskId)
            else {
                const result = await completeTask(task.TaskId)
                if (result?.NextRecurring) successToast(`Done. Next one is due ${formatShortDate(result.NextRecurring.DueDate)}`)
                else successToast('Follow-up completed')
            }
            invalidateCrm(...ALL_CRM_SCOPES)
        } catch (err) {
            showError(err)
            invalidateCrm('tasks', 'followups')
        } finally {
            setBusy(null)
        }
    }

    return (
        <section aria-label="Follow-ups" style={{ marginBottom: 16 }}>
            <h4 style={{ margin: '0 0 6px' }}>Follow-ups</h4>
            <AsyncState loading={tasks.loading} error={tasks.error} hasData={Boolean(tasks.data)} empty={Boolean(tasks.data) && list.length === 0} emptyText="No follow-ups set." onRetry={tasks.refetch}>
                {sorted.map(task => (
                    <Row key={task.TaskId} $between $gap={8} style={{ padding: '6px 0' }}>
                        <div style={{ textDecoration: task.IsDone ? 'line-through' : 'none', opacity: task.IsDone ? 0.6 : 1 }}>
                            <div>{task.Title}</div>
                            <Muted style={task.IsOverdue && !task.IsDone ? { color: '#dc2626', opacity: 1 } : undefined}>
                                {task.DueDate ? `${formatShortDate(task.DueDate)} · ${relativeDays(task.DueDate)}` : 'No date'}
                                {task.Recur ? ` · repeats ${task.Recur}` : ''}
                            </Muted>
                        </div>
                        <Button size="mini" basic loading={busy === task.TaskId} disabled={busy === task.TaskId} onClick={() => toggle(task)}>
                            {task.IsDone ? 'Reopen' : 'Done'}
                        </Button>
                    </Row>
                ))}
            </AsyncState>
        </section>
    )
}

const Timeline = ({ contactId }) => {
    const activities = useContactActivities(contactId)
    const [filter, setFilter] = useState('all')
    const all = activities.data || []
    const shown = filter === 'all' ? all : all.filter(a => a.Type === filter)

    return (
        <section aria-label="Timeline">
            <h4 style={{ margin: '0 0 6px' }}>Timeline</h4>
            <Row $gap={6} $wrap role="group" aria-label="Filter timeline" style={{ marginBottom: 6 }}>
                {TIMELINE_FILTERS.map(([key, text]) => (
                    <ChipButton key={key} type="button" $active={filter === key} aria-pressed={filter === key} onClick={() => setFilter(key)}>
                        {text}
                    </ChipButton>
                ))}
            </Row>
            <AsyncState loading={activities.loading} error={activities.error} hasData={Boolean(activities.data)} empty={Boolean(activities.data) && shown.length === 0} emptyText="No activity yet." onRetry={activities.refetch}>
                {shown.map(activity => (
                    <Entry key={activity.ActivityId}>
                        <div className="head">
                            <span>{activity.Title}</span>
                            <Muted>
                                {formatShortDate(activity.Date)} · {relativeDays(activity.Date)}
                            </Muted>
                        </div>
                        {activity.Body ? <div className="body">{activity.Body}</div> : null}
                        <Muted>{[TYPE_LABEL[activity.Type] || activity.Type, activity.ContactName, activity.OwnerName].filter(Boolean).join(' · ')}</Muted>
                    </Entry>
                ))}
            </AsyncState>
        </section>
    )
}

const CompanyTab = ({ accountId, onOpenContact }) => {
    const account = useAccountDetail(accountId)
    const detail = account.data
    const a = detail?.Account
    const site = safeLink(a?.Website)
    const others = (detail?.Contacts || []).filter(c => c.ContactId !== undefined)

    return (
        <AsyncState loading={account.loading} error={account.error} hasData={Boolean(detail)} onRetry={account.refetch}>
            {a ? (
                <div>
                    <Row $between $wrap>
                        <h3 style={{ margin: 0 }}>{a.Name}</h3>
                        <Pill $tone={HEALTH_TONE[a.Health?.Band] || 'gray'}>
                            {a.Health ? `${a.Health.Label} · ${a.Health.Score}` : 'No health data'}
                        </Pill>
                    </Row>
                    <Muted>{[a.Industry, a.City, a.AccountType].filter(Boolean).join(' · ')}</Muted>
                    {a.Website ? (
                        <div>
                            {site ? (
                                <a href={site} target="_blank" rel="noopener noreferrer">
                                    {a.Website}
                                </a>
                            ) : (
                                <span>{a.Website}</span>
                            )}
                        </div>
                    ) : null}
                    {a.Health?.Reasons?.length ? (
                        <ul style={{ margin: '10px 0', paddingLeft: 18 }}>
                            {a.Health.Reasons.map(reason => (
                                <li key={reason}>{reason}</li>
                            ))}
                        </ul>
                    ) : null}
                    {a.Sites?.length ? (
                        <p>
                            <strong>Sites:</strong> {a.Sites.join(', ')}
                        </p>
                    ) : null}
                    <p>
                        <strong>Open deals:</strong> {a.OpenDealCount} · {formatMoney(a.OpenDealAmount)}
                        {a.OwnerName ? ` · Owner ${a.OwnerName}` : ''}
                    </p>
                    <h4 style={{ margin: '12px 0 4px' }}>Contacts at {a.Name}</h4>
                    {others.map(c => (
                        <Row key={c.ContactId} $gap={8} style={{ padding: '4px 0' }}>
                            <Avatar name={c.Name} size={26} />
                            <button type="button" onClick={() => onOpenContact(c.ContactId)} style={{ background: 'none', border: 0, color: '#2563eb', cursor: 'pointer', padding: 0 }}>
                                {c.Name}
                            </button>
                            <Muted>{c.Title}</Muted>
                        </Row>
                    ))}
                </div>
            ) : null}
        </AsyncState>
    )
}

const DealsTab = ({ contactId }) => {
    const deals = useContactDeals(contactId)
    const list = deals.data || []
    return (
        <AsyncState loading={deals.loading} error={deals.error} hasData={Boolean(deals.data)} empty={Boolean(deals.data) && list.length === 0} emptyText="No deals for this contact." onRetry={deals.refetch}>
            {list.map(deal => (
                <Card key={deal.DealId} style={{ marginBottom: 8 }}>
                    <Row $between $wrap>
                        <strong>{deal.Name}</strong>
                        <StagePill stage={deal.Status === 'lost' ? 'Lost' : deal.Stage} />
                    </Row>
                    <Muted>
                        {formatMoney(deal.Amount)} · {deal.Pipeline}
                        {deal.CloseDate ? ` · Close ${formatShortDate(deal.CloseDate)}` : ''}
                        {deal.IsStuck ? ` · ${deal.DaysInStage} days in stage` : ''}
                    </Muted>
                </Card>
            ))}
        </AsyncState>
    )
}

/**
 * Contact detail drawer: Activity / Details / Company / Deals.
 * @param {{ contactId: number|null, onClose: () => void, onSwitch: (id: number) => void }} props
 */
export const ContactPanel = ({ contactId, onClose, onSwitch }) => {
    const [tab, setTab] = useState('activity')
    const [dialog, setDialog] = useState(null) // 'email' | 'meeting' | 'task'
    const contact = useContact(contactId)
    const deals = useContactDeals(contactId)
    // Ignore a stale record while another contact is loading.
    const c = contact.data && contact.data.ContactId === contactId ? contact.data : null

    // Opening another contact starts on the Activity tab.
    useEffect(() => {
        setTab('activity')
        setDialog(null)
    }, [contactId])

    const dealCount = deals.data ? deals.data.length : null

    return (
        <CrmDrawer open={Boolean(contactId)} onClose={onClose} title={c ? c.Name : 'Contact'} subtitle={c ? [c.Title, c.AccountName].filter(Boolean).join(' · ') : ''} width={600}>
            <AsyncState loading={contact.loading} error={contact.error} hasData={Boolean(c)} onRetry={contact.refetch}>
                {c ? (
                    <>
                        <Row $gap={10} $wrap style={{ marginBottom: 12 }}>
                            <Avatar name={c.Name} size={44} />
                            <StagePill stage={c.Stage} />
                            <Pill>{c.ContactType}</Pill>
                            {(c.Tags || []).map(tag => (
                                <Pill key={tag} $tone="blue">
                                    {tag}
                                </Pill>
                            ))}
                        </Row>
                        <Row $gap={12} $wrap style={{ marginBottom: 12 }}>
                            {c.Email ? <Muted>{c.Email}</Muted> : null}
                            {c.Phone ? <Muted>{c.Phone}</Muted> : null}
                            {c.OwnerName ? <Muted>Owner: {c.OwnerName}</Muted> : null}
                        </Row>

                        <Row $gap={8} $wrap role="group" aria-label="Contact actions" style={{ marginBottom: 12 }}>
                            <Button type="button" size="small" icon="mail" content="Email" onClick={() => setDialog('email')} />
                            <Button type="button" size="small" icon="calendar alternate outline" content="Meeting" onClick={() => setDialog('meeting')} />
                            <Button type="button" size="small" icon="bell outline" content="Follow-up" onClick={() => setDialog('task')} />
                        </Row>

                        <TabBar role="tablist" aria-label="Contact sections">
                            {TABS.map(([key, text]) => (
                                <TabButton key={key} type="button" role="tab" aria-selected={tab === key} $active={tab === key} onClick={() => setTab(key)}>
                                    {key === 'deals' && dealCount !== null ? `${text} (${dealCount})` : text}
                                </TabButton>
                            ))}
                        </TabBar>

                        <div role="tabpanel">
                            {tab === 'activity' ? (
                                <>
                                    <QuickLog contactId={c.ContactId} />
                                    <FollowUps contactId={c.ContactId} />
                                    <Timeline contactId={c.ContactId} />
                                </>
                            ) : null}
                            {tab === 'details' ? <ContactForm contact={c} formId="crm-contact-form-panel" onSaved={() => {}} /> : null}
                            {tab === 'company' ? <CompanyTab accountId={c.AccountId} onOpenContact={onSwitch} /> : null}
                            {tab === 'deals' ? <DealsTab contactId={c.ContactId} /> : null}
                        </div>
                        <EmailDialog contact={dialog === 'email' ? c : null} onClose={() => setDialog(null)} />
                        <MeetingDialog contact={dialog === 'meeting' ? c : null} onClose={() => setDialog(null)} />
                        <TaskDialog open={dialog === 'task'} contact={c} onClose={() => setDialog(null)} />
                    </>
                ) : null}
            </AsyncState>
        </CrmDrawer>
    )
}
